import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Phone, Mail, FileText } from 'lucide-react';
import { companyInfo } from '../data';
import useMeta from '../hooks/useMeta';

export default function Services() {
  useMeta(
    'Vores Services & Ydelser | LJR Teknik ApS',
    'Se vores brede udvalg af ydelser inden for service, reparation og vedligeholdelse af gaffeltrucks, stablere og entreprenørmaskiner.'
  );

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/services');
        if (res.ok) {
          const data = await res.json();
          setServices(data);
        }
      } catch (error) {
        console.error('Fejl ved hentning af services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const serviceAgreements = [
    "Forebyggende vedligeholdelse minimerer risikoen for uforudsete nedbrud.",
    "Automatisk indkaldelse til det lovpligtige årlige sikkerhedseftersyn.",
    "Prioriteret udrykning og hurtig hjælp, når uheldet er ude.",
    "Gennemskuelige priser og mulighed for fast månedlig eller årlig pris.",
    "Fuld dokumentation og kontrolbog udfyldt efter gældende regler."
  ];

  return (
    <div className="bg-zinc-50 min-h-screen">
      {/* Page Header */}
      <section className="bg-zinc-950 text-white py-16 md:py-20 border-b border-zinc-800">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl uppercase">
              Mine <span className="text-red-500">Serviceydelser</span>
            </h1>
            <p className="text-lg text-zinc-300 leading-relaxed">
              Jeg tilbyder professionel service, reparation og godkendelse af gaffeltrucks, stablere, pallevogne og mindre maskiner. Se mine ydelser nedenfor.
            </p>
          </div>
        </div>
      </section>

      {/* Services List Section */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          {loading ? (
            <div className="space-y-12">
              {[1, 2, 3].map((n) => (
                <div key={n} className="animate-pulse bg-white rounded-2xl border border-zinc-200 h-64" />
              ))}
            </div>
          ) : (
            <div className="space-y-12 md:space-y-16">
              {services.map((service, index) => {
                const title = service.title?.da || service.title || '';
                const desc = service.description?.da || service.description || '';
                const slug = service.slug?.da || service.slug || service.id;
                const imgUrl = service.image?.[0] || '';
                const isEven = index % 2 === 0;

                return (
                  <div
                    key={service.id}
                    className={`flex flex-col lg:flex-row gap-8 lg:gap-12 bg-white rounded-2xl border border-zinc-200 p-6 md:p-8 hover:shadow-md transition-all items-stretch ${
                      isEven ? '' : 'lg:flex-row-reverse'
                    }`}
                  >
                    {/* Image Column */}
                    <div className="w-full lg:w-1/2 min-h-[250px] md:min-h-[320px] relative rounded-xl overflow-hidden bg-zinc-100 shrink-0">
                      <img
                        src={imgUrl}
                        alt={title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>

                    {/* Content Column */}
                    <div className="w-full lg:w-1/2 flex flex-col justify-center py-2">
                      <h2 className="text-2xl font-semibold text-zinc-900 mb-4 uppercase">
                        {title}
                      </h2>
                      <p className="text-zinc-600 leading-relaxed mb-6">
                        {desc}
                      </p>
                      <div className="flex flex-wrap gap-4 mt-auto">
                        <Link
                          to={`/service/${slug}`}
                          className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-5 py-3 text-sm font-bold text-white hover:bg-zinc-800 transition-colors"
                        >
                          <span>Læs detaljeret beskrivelse</span>
                          <ArrowRight className="h-4 w-4 text-red-500" />
                        </Link>
                        <Link
                          to="/kontakt"
                          className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 transition-colors"
                        >
                          <span>Få et uforpligtende tilbud</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Service Agreement Section */}
      <section className="py-16 md:py-24 bg-zinc-900 text-white border-t border-zinc-800">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-400">
                Sikker drift og faste rammer
              </div>
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl uppercase">
                Få en skræddersyet <span className="text-red-500">Serviceaftale</span>
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                Med en fast serviceaftale hos mig slipper du for at bekymre dig om dine maskiners tilstand. Jeg holder styr på eftersynsdatoerne og sørger for løbende vedligeholdelse, så du kan fokusere på din kerneforretning.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-6 py-3 text-sm font-bold text-white hover:bg-red-700 shadow-lg shadow-red-600/20 transition-all"
                >
                  <Phone className="h-4 w-4" />
                  <span>Ring og hør mere: {companyInfo.phone}</span>
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 px-6 py-3 text-sm font-semibold text-white hover:bg-zinc-700 transition-all"
                >
                  <Mail className="h-4 w-4 text-red-500" />
                  <span>Skriv til mig</span>
                </Link>
              </div>
            </div>

            {/* Right Column */}
            <div className="bg-zinc-950 rounded-2xl border border-zinc-800 p-8 space-y-6">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2 uppercase">
                <FileText className="h-5 w-5 text-red-500" />
                <span>Hvad indeholder en aftale?</span>
              </h3>
              <ul className="space-y-4">
                {serviceAgreements.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-zinc-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
